import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  File,
  Eye,
  ThumbsUp,
  ThumbsDown,
  AlertTriangle,
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export default function PendingMemberPayments() {
  const queryClient = useQueryClient();
  const [selectedPayment, setSelectedPayment] = useState<any>(null);
  const [actionType, setActionType] = useState<"approve" | "reject" | null>(null);
  const [comment, setComment] = useState("");

  const { data: payments, isLoading } = useQuery(
    ["pending-member-payments"],
    async () => {
      const { data } = await api.get("/member-payments/pending");
      return data.data || [];
    },
    { refetchInterval: 15000 }
  );

  const actionMutation = useMutation(
    async ({ id, action }: { id: number; action: "approve" | "reject" }) => {
      const { data } = await api.patch(`/member-payments/${id}/${action}`, {
        comment: comment || undefined,
      });
      return data;
    },
    {
      onSuccess: (data, variables) => {
        toast.success(data?.message || `Payment ${variables.action === "approve" ? "approved" : "rejected"} successfully.`);
        setSelectedPayment(null);
        setActionType(null);
        setComment("");
        queryClient.invalidateQueries(["pending-member-payments"]);
      },
      onError: (err: any) => {
        const msg = err?.response?.data?.message || err?.message || "Action failed";
        toast.error(msg);
      },
    }
  );

  const pendingCount = payments?.length || 0;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Pending Member Payments</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Review and approve/reject member-submitted payments. Each payment needs {3} approvals.
          </p>
        </div>
        <Badge className={cn(
          "text-sm px-3 py-1",
          pendingCount > 0 ? "bg-amber-100 text-amber-800 border-amber-200" : "bg-green-100 text-green-800 border-green-200"
        )}>
          {pendingCount} pending
        </Badge>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-[#009A44]" />
        </div>
      ) : pendingCount === 0 ? (
        <Card className="shadow-sm border-slate-100">
          <CardContent className="flex flex-col items-center justify-center py-16 text-slate-400">
            <CheckCircle className="h-12 w-12 text-green-300 mb-3" />
            <p className="text-base font-medium text-slate-500">No pending payments</p>
            <p className="text-sm mt-1">All member submissions have been reviewed.</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="shadow-sm border-slate-100">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-slate-900">Submissions Awaiting Approval</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-y border-slate-200">
                  <tr>
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500">Member</th>
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500">Type</th>
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500">Paid Month</th>
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500">Amount</th>
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500">Method</th>
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500">Date</th>
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500">Approvals</th>
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500">Proof</th>
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {payments.map((payment: any) => (
                    <tr key={payment.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium text-slate-800 whitespace-nowrap">
                        {payment.memberName || `Member #${payment.memberId}`}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <Badge className="bg-blue-100 text-blue-800 border-blue-200 text-[10px] capitalize">
                          {payment.type === "saving" ? "Saving" : payment.type === "loan_payment" ? "Loan Payment" : "Penalty Payment"}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                        {MONTH_NAMES[payment.paidMonth]} {payment.paidYear}
                      </td>
                      <td className="px-4 py-3 font-semibold text-slate-900 whitespace-nowrap">
                        {payment.amount.toLocaleString()} FRW
                      </td>
                      <td className="px-4 py-3 text-slate-600">{payment.paymentMethod || "N/A"}</td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                        {format(new Date(payment.paymentDate), "MMM dd, yyyy")}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          <div className="flex -space-x-1">
                            {Array.from({ length: 3 }).map((_, i) => (
                              <div
                                key={i}
                                className={cn(
                                  "w-5 h-5 rounded-full border-2 border-white flex items-center justify-center",
                                  i < (payment.approvalCount || 0)
                                    ? "bg-green-500"
                                    : "bg-slate-200"
                                )}
                              >
                                {i < (payment.approvalCount || 0) ? (
                                  <CheckCircle className="h-3 w-3 text-white" />
                                ) : null}
                              </div>
                            ))}
                          </div>
                          <span className="text-xs text-slate-500 ml-1">
                            {payment.approvalCount || 0}/{payment.requiredApprovals || 3}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {payment.attachmentUrl ? (
                          <a
                            href={payment.attachmentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#009A44] hover:underline inline-flex items-center gap-1 text-xs"
                          >
                            <File className="h-3.5 w-3.5" /> View
                          </a>
                        ) : (
                          <span className="text-slate-300 text-xs">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => { setSelectedPayment(payment); setActionType("approve"); setComment(""); }}
                            className="h-8 text-xs border-green-200 text-green-700 hover:bg-green-50 hover:text-green-800"
                          >
                            <ThumbsUp className="h-3.5 w-3.5 mr-1" /> Approve
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => { setSelectedPayment(payment); setActionType("reject"); setComment(""); }}
                            className="h-8 text-xs border-red-200 text-red-600 hover:bg-red-50 hover:text-red-800"
                          >
                            <ThumbsDown className="h-3.5 w-3.5 mr-1" /> Reject
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setSelectedPayment(payment); setActionType(null); setComment(""); }}
                            className="h-8 text-xs text-slate-500"
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Review Dialog */}
      <Dialog open={!!selectedPayment} onOpenChange={(open) => { if (!open) { setSelectedPayment(null); setActionType(null); setComment(""); } }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-base">
              {actionType === "approve" ? "Approve Payment" : actionType === "reject" ? "Reject Payment" : "Payment Details"}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {actionType === "approve" ? "Confirm approval of this member payment." :
               actionType === "reject" ? "Provide a reason for rejection." :
               "Review the payment details."}
            </DialogDescription>
          </DialogHeader>

          {selectedPayment && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm bg-slate-50 rounded-lg p-3">
                <div>
                  <span className="text-slate-400 text-xs block">Member</span>
                  <span className="font-semibold">{selectedPayment.memberName || `Member #${selectedPayment.memberId}`}</span>
                </div>
                <div>
                  <span className="text-slate-400 text-xs block">Type</span>
                  <Badge className="bg-blue-100 text-blue-800 border-blue-200 text-[10px] capitalize mt-0.5">
                    {selectedPayment.type === "saving" ? "Saving" : selectedPayment.type === "loan_payment" ? "Loan Payment" : "Penalty Payment"}
                  </Badge>
                </div>
                <div>
                  <span className="text-slate-400 text-xs block">Amount</span>
                  <span className="font-semibold">{selectedPayment.amount.toLocaleString()} FRW</span>
                </div>
                <div>
                  <span className="text-slate-400 text-xs block">Paid Month</span>
                  <span className="font-semibold">{MONTH_NAMES[selectedPayment.paidMonth]} {selectedPayment.paidYear}</span>
                </div>
                <div>
                  <span className="text-slate-400 text-xs block">Method</span>
                  <span className="font-semibold">{selectedPayment.paymentMethod || "N/A"}</span>
                </div>
                <div>
                  <span className="text-slate-400 text-xs block">Payment Date</span>
                  <span className="font-semibold">{format(new Date(selectedPayment.paymentDate), "MMM dd, yyyy")}</span>
                </div>
                {selectedPayment.notes && (
                  <div className="col-span-2">
                    <span className="text-slate-400 text-xs block">Notes</span>
                    <span className="text-sm">{selectedPayment.notes}</span>
                  </div>
                )}
                {selectedPayment.attachmentUrl && (
                  <div className="col-span-2">
                    <span className="text-slate-400 text-xs block">Attachment</span>
                    <a
                      href={selectedPayment.attachmentUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#009A44] hover:underline inline-flex items-center gap-1 text-sm mt-0.5"
                    >
                      <File className="h-4 w-4" /> View Attachment
                    </a>
                  </div>
                )}
              </div>

              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-2">Approval Progress ({selectedPayment.approvalCount || 0}/{selectedPayment.requiredApprovals || 3})</h4>
                <div className="space-y-2">
                  {(!selectedPayment.approvals || selectedPayment.approvals.length === 0) ? (
                    <p className="text-xs text-slate-400">No approvals yet.</p>
                  ) : (
                    selectedPayment.approvals.map((a: any) => (
                      <div key={a.id} className="flex items-start gap-3 border border-slate-100 rounded-lg p-2.5">
                        <div className={cn(
                          "w-6 h-6 rounded-full flex items-center justify-center shrink-0",
                          a.status === "approved" ? "bg-green-100" : "bg-red-100"
                        )}>
                          {a.status === "approved" ? (
                            <CheckCircle className="h-3.5 w-3.5 text-green-600" />
                          ) : (
                            <XCircle className="h-3.5 w-3.5 text-red-600" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-700">{a.leaderName || `Leader #${a.leaderId}`}</p>
                          {a.comment && <p className="text-xs text-slate-500">{a.comment}</p>}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {actionType && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    {actionType === "approve" ? "Comment (optional)" : "Rejection Reason *"}
                  </Label>
                  <Textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder={actionType === "approve" ? "Optional comment..." : "Provide a reason for rejection..."}
                    className="rounded-lg text-sm"
                    rows={2}
                  />
                </div>
              )}

              {actionType === "approve" && (
                <Alert className="bg-green-50 border-green-200">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-xs text-green-700">
                    This payment will be marked as approved by you. After {selectedPayment.requiredApprovals || 3} approvals, it will be automatically applied.
                  </AlertDescription>
                </Alert>
              )}

              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => { setSelectedPayment(null); setActionType(null); setComment(""); }}
                  className="flex-1 h-10"
                >
                  Cancel
                </Button>
                {actionType === "approve" && (
                  <Button
                    onClick={() => actionMutation.mutate({ id: selectedPayment.id, action: "approve" })}
                    disabled={actionMutation.isLoading}
                    className="flex-1 h-10 bg-green-600 hover:bg-green-700 text-white"
                  >
                    {actionMutation.isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ThumbsUp className="h-4 w-4 mr-1" /> Confirm Approval</>}
                  </Button>
                )}
                {actionType === "reject" && (
                  <Button
                    onClick={() => actionMutation.mutate({ id: selectedPayment.id, action: "reject" })}
                    disabled={actionMutation.isLoading || !comment.trim()}
                    className="flex-1 h-10 bg-red-600 hover:bg-red-700 text-white"
                  >
                    {actionMutation.isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ThumbsDown className="h-4 w-4 mr-1" /> Confirm Rejection</>}
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
